import './styles.scss'

import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { clsx } from 'clsx'

import Translator from '@/components/B2becTranslation'
import CustomButton from '@/components/CustomButton'
import { ChevronLeftIcon } from '@/components/Icons'
import { useAppSelector } from '@/store/hook'
import { selectIsCurrentTimeInSAPDowntime } from '@/store/slices/configSlice'
import { useCheckoutContext } from '@/contexts/checkout/useCheckoutContext'
import { linkGenerator } from '@/libs/utils/language'

interface PlaceOrderButtonProps {
  submitFormHandler?: (...args: unknown[]) => unknown
  className?: string
  disabled?: boolean
}

export const PlaceOrderButton = (props: PlaceOrderButtonProps) => {
  const { submitFormHandler, className, disabled } = props

  return (
    <CustomButton
      className={`button-wrapper__button button-wrapper__button--next ${className}`}
      disabled={disabled}
      onClick={submitFormHandler}
    >
      <Translator value="checkout.step3.navigationButtons.next" />
    </CustomButton>
  )
}

interface ButtonsWrapperComponentProps {
  tab?: number
  setTabHandler?: (...args: unknown[]) => unknown
  submitFormHandler?: (...args: unknown[]) => unknown
}

const ButtonsWrapperComponent = ({
  tab = 1,
  setTabHandler,
  submitFormHandler,
}: ButtonsWrapperComponentProps) => {
  const navigate = useNavigate()

  const { shouldDisableCheckout } = useCheckoutContext()

  const isCurrentTimeInSAPDowntime = useAppSelector(
    selectIsCurrentTimeInSAPDowntime,
  )

  const activePreviousTabHandler = () => {
    switch (tab) {
      case 1: {
        navigate(linkGenerator('/cart'))
        break
      }
      default: {
        setTabHandler?.(+(tab - 1))
        break
      }
    }
  }

  const navigationButtonTitleKeys = useMemo(() => {
    switch (tab) {
      case 1: {
        return {
          back: 'checkout.step1.navigationButtons.back',
          next: 'checkout.step1.navigationButtons.next',
        }
      }
      case 2: {
        return {
          back: 'checkout.step2.navigationButtons.back',
          next: 'checkout.step2.navigationButtons.next',
        }
      }
      case 3: {
        return {
          back: 'checkout.step3.navigationButtons.back',
          next: 'checkout.step3.navigationButtons.next',
        }
      }
      default: {
        return {
          back: 'checkout.navigationButtons.back',
          next: 'checkout.navigationButtons.next',
        }
      }
    }
  }, [tab])

  return (
    <div className="button-wrapper">
      <CustomButton
        icon={ChevronLeftIcon}
        type="ghost"
        className={clsx(
          'button-wrapper__button',
          'button-wrapper__button--back',
          {
            'button-wrapper__icon': tab === 1,
          },
        )}
        onClick={activePreviousTabHandler}
      >
        <Translator value={navigationButtonTitleKeys.back} />
      </CustomButton>
      {tab >= 3
        ? (
          <PlaceOrderButton
            disabled={shouldDisableCheckout || isCurrentTimeInSAPDowntime}
            submitFormHandler={submitFormHandler}
          />
        )
        : (
          <CustomButton
            className="button-wrapper__button button-wrapper__button--next"
            onClick={() => setTabHandler?.(+(tab + 1))}
          >
            <Translator value={navigationButtonTitleKeys.next} />
          </CustomButton>
        )}
    </div>
  )
}

export default ButtonsWrapperComponent
